import type React from 'react';
import { createContext, useContext } from 'react';

import {
  type AtlasGqlInspectionType,
  type AtlasGqlMetadataFieldMapping,
  AtlasGqlFileFormat,
  AtlasGqlInspectionStatus,
} from 'types/atlas-graphql';

import { type Steps } from '../UploadInspectionDataModal';
/**
 * Jo 1/8/26 - Adding this Context as part of addressing sc-149527 with the intent of coming back
 * and refactoring most of the state-management used in UploadInspectionDataModal to be owned by this context, rather than the Modal itself
 * For this first pass, just adding in the error messaging
 */
export type UploadInspectionDataModalContextErrorState = {
  alertTitle?: string;
  alertDescription: string;
  alertType: 'error' | 'warning';
};

export type ErroredUploadSteps = {
  summary: boolean;
  imageDetails: boolean;
};

interface imageDetailsContext {
  file: File | undefined;
  setFile: React.Dispatch<React.SetStateAction<File | undefined>>;
  fieldMappings: AtlasGqlMetadataFieldMapping[] | undefined;
  setFieldMappings: React.Dispatch<React.SetStateAction<AtlasGqlMetadataFieldMapping[]>>;
  providedFieldNames: string[];
  setProvidedFieldNames: React.Dispatch<React.SetStateAction<string[]>>;
  imagePathFieldName: string;
  setImagePathFieldName: React.Dispatch<React.SetStateAction<string>>;
  previewData: {
    [key: string]: string | number;
  }[];
  setPreviewData: React.Dispatch<
    React.SetStateAction<
      {
        [key: string]: string | number;
      }[]
    >
  >;
  imageDetailsFileType: AtlasGqlFileFormat;
  setImageDetailsFileType: React.Dispatch<React.SetStateAction<AtlasGqlFileFormat>>;
  someImagesMatchingMetadata: boolean;
  setSomeImagesMatchingMetadata: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface UploadInspectionDataModalContextType {
  // When populated, will be displayed as a top level Alert in the modal
  alertState: UploadInspectionDataModalContextErrorState | null;
  setAlertState: React.Dispatch<
    React.SetStateAction<UploadInspectionDataModalContextErrorState | null>
  >;
  shouldAutoRelease: boolean;
  setShouldAutoRelease: React.Dispatch<React.SetStateAction<boolean>>;
  autoReleaseStatus: AtlasGqlInspectionStatus;
  setAutoReleaseStatus: React.Dispatch<React.SetStateAction<AtlasGqlInspectionStatus>>;
  useFolderMethod: boolean;
  setUseFolderMethod: React.Dispatch<React.SetStateAction<boolean>>;
  pendingStep: Steps | undefined;
  setPendingStep: React.Dispatch<React.SetStateAction<Steps | undefined>>;
  erroredUploadSteps: ErroredUploadSteps;
  setErroredUploadSteps: React.Dispatch<React.SetStateAction<ErroredUploadSteps>>;
  inspectionType: AtlasGqlInspectionType | undefined;
  setInspectionType: React.Dispatch<React.SetStateAction<AtlasGqlInspectionType | undefined>>;
  createdInspectionIds: string[];
  setCreatedInspectionIds: React.Dispatch<React.SetStateAction<string[]>>;
  createdTaskIds: string[];
  setCreatedTaskIds: React.Dispatch<React.SetStateAction<string[]>>;
  imageDetailsContext: imageDetailsContext;
  resetState: () => void;
}

export const defaultContext: UploadInspectionDataModalContextType = {
  alertState: null,
  setAlertState: () => {},
  shouldAutoRelease: false,
  setShouldAutoRelease: () => {},
  autoReleaseStatus: AtlasGqlInspectionStatus.AnalysisPending,
  setAutoReleaseStatus: () => {},
  useFolderMethod: false,
  setUseFolderMethod: () => {},
  pendingStep: undefined,
  setPendingStep: () => {},
  erroredUploadSteps: { summary: false, imageDetails: false },
  setErroredUploadSteps: () => {},
  inspectionType: undefined,
  setInspectionType: () => {},
  createdInspectionIds: [],
  setCreatedInspectionIds: () => {},
  createdTaskIds: [],
  setCreatedTaskIds: () => {},
  imageDetailsContext: {
    file: undefined,
    setFile: () => {},
    fieldMappings: undefined,
    setFieldMappings: () => {},
    providedFieldNames: [],
    setProvidedFieldNames: () => {},
    imagePathFieldName: '',
    setImagePathFieldName: () => {},
    previewData: [],
    setPreviewData: () => {},
    imageDetailsFileType: AtlasGqlFileFormat.Csv,
    setImageDetailsFileType: () => {},
    someImagesMatchingMetadata: true,
    setSomeImagesMatchingMetadata: () => {},
  },
  resetState: () => {},
};

export const UploadInspectionDataModalContext =
  createContext<UploadInspectionDataModalContextType>(defaultContext);

export function useUploadInspectionDataModalContext() {
  return useContext(UploadInspectionDataModalContext);
}
